// === IMPORTS ===
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { SystemTimezone } from '../types';
import {
  formatTimeInTimezone, getTimezoneOffsetDisplay, POPULAR_IANA_TIMEZONES,
  convertClockTimeBetweenTimezones, getBrowserTimezone,
} from '../utils/timezoneUtils';
import { Clock, Globe, Plus, Trash2, Check, Search, History, X, ArrowRight, ChevronDown } from 'lucide-react';
import '../styles/twin-drawers-shared.css';
import '../styles/twin-icons-shared.css';
import './TimezoneManagerModal.css';

interface TimezoneManagerDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  systemTimezones?: SystemTimezone[];
  timezones?: SystemTimezone[];
  activeTimezoneIana: string;
  onSelectTimezone?: (iana: string) => void;
  onSelectActiveTimezone?: (iana: string) => void;
  onAddTimezone: (tz: SystemTimezone) => void;
  onDeleteTimezone: (id: string) => void;
  currentLocationName?: string;
  currentLocationTimezone?: string;
  onJumpToSimulatedTime?: (targetClockTime: string) => void;
  initialTab?: 'manage' | 'add' | 'converter';
}

export type TimezoneManagerModalProps = TimezoneManagerDrawerProps;

// === COMPONENTE: GESTOR DE ZONAS HORARIAS DEL SISTEMA ===
export const TimezoneManagerDrawer: React.FC<TimezoneManagerDrawerProps> = ({
  isOpen, onClose, systemTimezones, timezones, activeTimezoneIana,
  onSelectTimezone, onSelectActiveTimezone, onAddTimezone, onDeleteTimezone,
  currentLocationName, currentLocationTimezone, onJumpToSimulatedTime, initialTab = 'manage',
}) => {
  const tzList = systemTimezones || timezones || [];
  const handleSelectTz = onSelectTimezone || onSelectActiveTimezone || (() => {});

  const [activeTab, setActiveTab] = useState<'manage' | 'add' | 'converter'>(initialTab);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [now, setNow] = useState<Date>(new Date());
  const drawerRef = useRef<HTMLElement>(null);
  const [isZoneDropdownOpen, setIsZoneDropdownOpen] = useState(false);
  const zoneDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    const handlePointerDown = (e: MouseEvent | TouchEvent | PointerEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (drawerRef.current && drawerRef.current.contains(target)) return;
      if (target.closest('#confirm-delete-modal-overlay') || target.closest('#confirm-delete-modal-container')) return;
      onClose();
    };
    const handleKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('pointerdown', handlePointerDown, true);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);
  
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (zoneDropdownRef.current && !zoneDropdownRef.current.contains(e.target as Node)) {
        setIsZoneDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => { if (isOpen && initialTab) setActiveTab(initialTab); }, [isOpen, initialTab]);

  const [selectedIanaToAdd, setSelectedIanaToAdd] = useState<string>('America/Mexico_City');
  const [customName, setCustomName] = useState<string>('');
  const [customFlag, setCustomFlag] = useState<string>('🇲🇽');

  const [converterInputTime, setConverterInputTime] = useState<string>('17:00');
  const [converterSourceIana, setConverterSourceIana] = useState<string>(tzList[0]?.iana || 'Europe/Madrid');
  const [converterTargetIana, setConverterTargetIana] = useState<string>(activeTimezoneIana);

  useEffect(() => {
    if (!isOpen) return;
    const timer = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, [isOpen]);

  useEffect(() => { setConverterTargetIana(activeTimezoneIana); }, [activeTimezoneIana]);

  const userLocalTz = useMemo(() => getBrowserTimezone(), []);

  const filteredPopularZones = useMemo(() => {
    const q = searchQuery.toLowerCase().trim();
    if (!q) return POPULAR_IANA_TIMEZONES;
    return POPULAR_IANA_TIMEZONES.filter((tz) =>
      tz.name.toLowerCase().includes(q) || tz.city.toLowerCase().includes(q) ||
      tz.country.toLowerCase().includes(q) || tz.iana.toLowerCase().includes(q)
    );
  }, [searchQuery]);

  const handleSelectPredefinedForAdd = (item: (typeof POPULAR_IANA_TIMEZONES)[0]) => {
    setSelectedIanaToAdd(item.iana);
    setCustomName(item.name);
    setCustomFlag(item.flag);
  };

  const handleCreateTimezone = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedIanaToAdd) return;
    const matched = POPULAR_IANA_TIMEZONES.find((p) => p.iana === selectedIanaToAdd);
    const newTz: SystemTimezone = {
      id: `tz_${Date.now()}_${Math.floor(Math.random() * 1000)}`,
      name: customName.trim() || matched?.name || selectedIanaToAdd,
      iana: selectedIanaToAdd,
      flag: customFlag.trim() || matched?.flag || '🌐',
      city: matched?.city,
      country: matched?.country,
      isCustom: true,
    };
    onAddTimezone(newTz);
    setCustomName('');
    setActiveTab('manage');
  };

  const conversionResult = convertClockTimeBetweenTimezones(converterInputTime, converterSourceIana, converterTargetIana, now);

  const activeTzObj = tzList.find((t) => t.iana === activeTimezoneIana) || { name: activeTimezoneIana, flag: '🌐', iana: activeTimezoneIana };

  // === RENDER ===
  return (
    <aside ref={drawerRef} id="timezone-manager-drawer" aria-label="Sidebar: Timezone Manager" className={`twin-drawer twin-drawer--lg twin-tzmodal ${isOpen ? 'twin-drawer--open' : 'twin-drawer--closed'}`}>
      {/* === HEADER === */}
      <div className="twin-drawer-header">
        <div className="twin-drawer-header-left">
          <div className="twin-drawer-icon-badge twin-tzmodal-icon-badge">
            <Globe className="twin-icon-sm" />
          </div>
          <div className="twin-drawer-header-text">
            <h2 className="twin-drawer-title">
              <span>Timezones</span>
              <span className="twin-tzmodal-count-tag">{tzList.length} zones</span>
            </h2>
            <p className="twin-drawer-subtitle twin-timezone-grid-badge">System time and simulation clock</p>
          </div>
        </div>
        <button type="button" onClick={onClose} className="twin-drawer-close-btn" title="Close drawer (Esc)"><X className="twin-icon-sm" /></button>
      </div>

      {/* === BANNER DE ZONA ACTIVA === */}
      <div className="twin-tzmodal-active-banner">
        <div className="twin-tzmodal-active-left">
          <Globe className="twin-icon-sm twin-color-theme-accent" />
          <span className="twin-tzmodal-active-label">Active Zone:</span>
          <strong className="twin-tzmodal-active-name">{activeTzObj.name}</strong>
          <span className="twin-tzmodal-active-offset">{getTimezoneOffsetDisplay(activeTimezoneIana, now)}</span>
        </div>
        <div className="twin-tzmodal-active-clock"><Clock className="twin-icon-sm twin-color-theme-accent" /><span>{formatTimeInTimezone(now, activeTimezoneIana)}</span></div>
      </div>

      {/* === PESTAÑAS === */}
      <div className="twin-tzmodal-tabs">
        <button type="button" onClick={() => setActiveTab('manage')} className={`twin-tzmodal-tab twin-tzmodal-tab--cyan ${activeTab === 'manage' ? 'twin-tzmodal-tab--active-cyan' : ''}`}>
          <Clock className="twin-icon-sm" /><span>Active Zones ({tzList.length})</span>
        </button>
        <button type="button" onClick={() => setActiveTab('add')} className={`twin-tzmodal-tab ${activeTab === 'add' ? 'twin-tzmodal-tab--active-emerald' : ''}`}>
          <Plus className="twin-icon-sm" /><span>Add Timezone</span>
        </button>
        <button type="button" onClick={() => setActiveTab('converter')} className={`twin-tzmodal-tab twin-tzmodal-tab--amber ${activeTab === 'converter' ? 'twin-tzmodal-tab--active-amber' : ''}`}>
          <History className="twin-icon-sm" /><span>Time Converter</span>
        </button>
      </div>

      {/* === CUERPO === */}
      <div className="twin-drawer-body">
        {/* === TAB 1: GESTIONAR === */}
        {activeTab === 'manage' && (
          <div className="twin-section-gap">
            <div className="twin-tzmodal-manage-hint">
              <span>Click <strong>Activate in System</strong> to toggle system time:</span>
              <button type="button" onClick={() => setActiveTab('add')} className="twin-tzmodal-add-link"><Plus className="twin-icon-sm" /> Add</button>
            </div>

            <div className="twin-section-gap">
              {tzList.map((tz) => {
                const isActive = tz.iana === activeTimezoneIana;
                const isCurrentFloorTz = currentLocationTimezone === tz.iana;
                const isUserBrowserTz = userLocalTz === tz.iana;
                const liveTime = formatTimeInTimezone(now, tz.iana);
                const offsetStr = getTimezoneOffsetDisplay(tz.iana, now);

                return (
                  <div key={tz.id} className={`twin-tzmodal-tz-row ${isActive ? 'twin-tzmodal-tz-row--active' : ''}`}>
                    <div className="twin-tzmodal-tz-left">
                      <div className={`twin-tzmodal-tz-icon ${isActive ? 'twin-tzmodal-tz-icon--active' : ''}`}>
                        <Globe className="twin-icon-sm" />
                      </div>
                      <div className="twin-tzmodal-tz-info">
                        <div className="twin-tzmodal-tz-name-row">
                          <span className="twin-tzmodal-tz-name">{tz.name}</span>
                          {isActive && <span className="twin-tzmodal-badge twin-tzmodal-badge--active">Active</span>}
                          {isCurrentFloorTz && <span className="twin-tzmodal-badge twin-tzmodal-badge--floor" title={`Default timezone for floor ${currentLocationName || 'active'}`}>Floor Default</span>}
                          {isUserBrowserTz && <span className="twin-tzmodal-badge twin-tzmodal-badge--user">Your Time</span>}
                        </div>
                        <div className="twin-tzmodal-tz-meta">
                          <span className="twin-color-theme-accent">{tz.iana}</span><span>•</span>
                          <span className="twin-tzmodal-offset-chip">{offsetStr}</span>
                        </div>
                      </div>
                    </div>

                    <div className="twin-tzmodal-tz-right">
                      <div className="twin-tzmodal-live-clock">
                        <span className="twin-tzmodal-live-time">{liveTime}</span>
                        <span className="twin-tzmodal-live-label">Local Time</span>
                      </div>
                      {isActive ? (
                        <div className="twin-tzmodal-active-tag"><Check className="twin-icon-xs" /> Active</div>
                      ) : (
                        <button type="button" onClick={() => handleSelectTz(tz.iana)} className="twin-tzmodal-activate-btn">Activate</button>
                      )}
                      {tzList.length > 1 && (
                        <button type="button" onClick={() => onDeleteTimezone(tz.id)} className="twin-tzmodal-delete-btn" title="Delete timezone from list"><Trash2 className="twin-icon-sm" /></button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* === TAB 2: AGREGAR === */}
        {activeTab === 'add' && (
          <div className="twin-section-gap">
            <div className="twin-drawer-card">
              <span className="twin-section-heading twin-tzmodal-label-style">1. Search Country, City or IANA Code</span>

              <div className="twin-tzmodal-zone-selector" ref={zoneDropdownRef}>
                <button
                  type="button"
                  onClick={() => setIsZoneDropdownOpen((prev) => !prev)}
                  className="twin-tzmodal-zone-selector-btn"
                >
                  <div className="twin-tzmodal-zone-selector-btn__left">
                    <span className="twin-tzmodal-zone-selector-btn__icon">{customFlag || '🌐'}</span>
                    <div className="twin-tzmodal-zone-selector-btn__info">
                      <span className="twin-tzmodal-zone-selector-btn__name">{customName || selectedIanaToAdd}</span>
                      <span className="twin-tzmodal-zone-selector-btn__desc">{getTimezoneOffsetDisplay(selectedIanaToAdd, now)}</span>
                    </div>
                  </div>
                  <ChevronDown
                    size={18}
                    className={`twin-tzmodal-zone-selector-btn__chevron ${isZoneDropdownOpen ? 'twin-tzmodal-zone-selector-btn__chevron--open' : ''}`}
                  />
                </button>

                {isZoneDropdownOpen && (
                  <div className="twin-tzmodal-zone-dropdown">
                    <div className="twin-tzmodal-zone-dropdown__search">
                      <Search className="twin-icon-sm twin-tzmodal-search-icon" />
                      <input
                        type="text"
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="New York, London, Tokyo, Chicago, Paris, UTC..."
                        className="twin-input twin-tzmodal-search-input"
                      />
                    </div>
                    <div className="twin-tzmodal-zone-dropdown__list">
                      {filteredPopularZones.map((item) => {
                        const isSelected = selectedIanaToAdd === item.iana;
                        const offset = getTimezoneOffsetDisplay(item.iana, now);
                        return (
                          <button
                            key={item.iana}
                            type="button"
                            onClick={() => {
                              handleSelectPredefinedForAdd(item);
                              setIsZoneDropdownOpen(false);
                            }}
                            className={`twin-tzmodal-zone-dropdown-option ${isSelected ? 'twin-tzmodal-zone-dropdown-option--active' : ''}`}
                          >
                            <span className="twin-tzmodal-zone-dropdown-option__icon">{item.flag}</span>
                            <div className="twin-tzmodal-zone-dropdown-option__info">
                              <span className="twin-tzmodal-zone-dropdown-option__name">{item.city}</span>
                              <span className="twin-tzmodal-zone-dropdown-option__desc">{offset}</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              {/* === BLOQUE ANTERIOR — comentado, no borrado, por si se necesita restaurar el grid de chips ===
              <div className="twin-tzmodal-search-wrap">
                <Search className="twin-icon-sm twin-tzmodal-search-icon" />
                <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="New York, London, Tokyo, Chicago, Paris, UTC..." className="twin-input twin-tzmodal-search-input" />
              </div>
              <div className="twin-tzmodal-zone-grid">
                {filteredPopularZones.map((item) => { ... })}
              </div>
              === FIN BLOQUE ANTERIOR === */}
            </div>

            <form onSubmit={handleCreateTimezone} className="twin-drawer-card">
              <span className="twin-section-heading twin-tzmodal-label-style">2. Custom Configuration & Alias</span>

              <div className="twin-grid-3">
                <div className="twin-col-span-2">
                  <label className="twin-field-label">Timezone Name / Alias:</label>
                  <input type="text" value={customName} onChange={(e) => setCustomName(e.target.value)} placeholder="e.g. Miami HQ, Cloud Cluster..." className="twin-input twin-input--tz" />
                </div>
                <div>
                  <label className="twin-field-label">Flag / Icon:</label>
                  <input type="text" value={customFlag} onChange={(e) => setCustomFlag(e.target.value)} className="twin-input twin-input--tz twin-text-center" maxLength={4} />
                </div>
              </div>

              <div>
                <label className="twin-field-label">Standard IANA Identifier:</label>
                <input type="text" value={selectedIanaToAdd} onChange={(e) => setSelectedIanaToAdd(e.target.value)} className="twin-input twin-input--tz twin-tzmodal-iana-input" required />
              </div>

              <div className="twin-tzmodal-form-actions">
                <button type="button" onClick={() => setActiveTab('manage')} className="twin-link-btn">Cancel</button>
                <button type="submit" className="twin-btn twin-btn--emerald-primary"><Plus className="twin-icon-sm" /> Add Timezone</button>
              </div>
            </form>
          </div>
        )}

        {/* === TAB 3: CONVERSOR === */}
        {activeTab === 'converter' && (
          <div className="twin-section-gap">
            <div className="twin-drawer-card twin-tzmodal-converter-panel">
              <div className="twin-tzmodal-converter-header">
                <History className="twin-icon-sm twin-color-amber" />
                <h3 className="twin-tzmodal-converter-title">Time Converter & Time Machine DVR</h3>
              </div>
              <p className="twin-tzmodal-converter-desc">Compute the time difference between any international zone and system time to jump directly to that moment in Time Machine DVR.</p>

              <div className="twin-section-gap">
                <div className="twin-tzmodal-converter-field">
                  <label className="twin-tzmodal-converter-label">1. Source Time:</label>
                  <input type="time" step="1" value={converterInputTime} onChange={(e) => setConverterInputTime(e.target.value)} className="twin-tzmodal-time-input" />
                </div>

                <div className="twin-grid-2">
                  <div className="twin-tzmodal-converter-field">
                    <label className="twin-tzmodal-converter-label">2. Source Timezone:</label>
                    <select value={converterSourceIana} onChange={(e) => setConverterSourceIana(e.target.value)} className="twin-select twin-tzmodal-select">
                      {tzList.map((tz) => <option key={tz.id} value={tz.iana}>{tz.name} ({getTimezoneOffsetDisplay(tz.iana, now)})</option>)}
                    </select>
                  </div>
                  <div className="twin-tzmodal-converter-field">
                    <label className="twin-tzmodal-converter-label">3. In System:</label>
                    <select value={converterTargetIana} onChange={(e) => setConverterTargetIana(e.target.value)} className="twin-select twin-tzmodal-select twin-color-theme-accent">
                      {tzList.map((tz) => <option key={tz.id} value={tz.iana}>{tz.name} ({getTimezoneOffsetDisplay(tz.iana, now)})</option>)}
                    </select>
                  </div>
                </div>
              </div>

              <div className="twin-tzmodal-conversion-card">
                <div className="twin-tzmodal-conversion-side">
                  <span className="twin-tzmodal-conversion-label">Source Time</span>
                  <span className="twin-tzmodal-conversion-value twin-color-amber">{converterInputTime}</span>
                </div>
                <div className="twin-tzmodal-conversion-arrow">
                  <ArrowRight className="twin-icon-sm" />
                  <span className="twin-tzmodal-conversion-diff">{conversionResult.diffHours >= 0 ? `+${conversionResult.diffHours.toFixed(1)}h` : `${conversionResult.diffHours.toFixed(1)}h`}</span>
                </div>
                <div className="twin-tzmodal-conversion-side">
                  <span className="twin-tzmodal-conversion-label twin-color-theme-accent">In System</span>
                  <span className="twin-tzmodal-conversion-value twin-color-theme-accent">
                    {conversionResult.convertedClockTime}
                    {conversionResult.dayShift !== 0 && <span className="twin-tzmodal-dayshift">({conversionResult.dayShift > 0 ? '+1d' : '-1d'})</span>}
                  </span>
                </div>
              </div>

              {onJumpToSimulatedTime && (
                <div className="twin-tzmodal-jump-row">
                  <button type="button" onClick={() => { onJumpToSimulatedTime(conversionResult.convertedClockTime); onClose(); }} className="twin-btn twin-btn--amber-primary twin-tzmodal-jump-btn">
                    <History className="twin-icon-sm" /><span>Jump to {conversionResult.convertedClockTime} in Time Machine</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* === FOOTER === */}
      <div className="twin-drawer-footer twin-drawer-footer-info">
        <span className="twin-tzmodal-footer-sync">
          <Globe className="twin-icon-sm twin-color-theme-accent" /> Real-time synchronized
        </span>
        <button type="button" onClick={onClose} className="twin-btn-cancel">
          Close
        </button>
      </div>
    </aside>
  );
};

// === ALIAS DE COMPATIBILIDAD ===
export const TimezoneManagerModal = TimezoneManagerDrawer;